import Button from "../../custom/button/button";
import laptop from "../../assets/laptop.png";
import success from "../../assets/success.png";
import warning from "../../assets/warning.png";
import points from "../../assets/points.png";
import styles from "./main.module.scss";
import { ReactComponent as Calendar } from "../../assets/calendar2.svg";
import { ReactComponent as Clock } from "../../assets/clock.svg";
import { ReactComponent as Marker } from "../../assets/marker.svg";
import { App, Modal, Spin } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import ProfileCard from "../profileCard/profileCard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MemberType,
  deleteWorkActivity,
  getCutOffScores,
  getHRWorkActivity,
  getMembershipUpgradeStatus,
  requestMembershipUpgrade,
} from "../../requests";
import { useAtomValue } from "jotai";
import { userData, userProfile } from "../../state";
import { errorMessage } from "../../utils/errorMessage";
import { AxiosError } from "axios";
import { formatDate, getDateWithoutTime } from "../../utils/formatDate";
import Table, { ColumnsType } from "antd/es/table";
import EndorsementModal from "./endorsementModal";
import AddActivity from "./addActivity";
import { useRequery } from "../../hooks/useRequery";

const content = (
  <div className={styles.modalContent}>
    <img src={success} alt="success" />
    <h2>Congratulations!!!</h2>
    <p>
      You have successfully paid for membership upgrade. Your certificate is now
      processing; you will be notified when it's ready!
    </p>
  </div>
);

const Main = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useAtomValue(userData);
  const profile = useAtomValue(userProfile);
  const [openRequirements, setOpenRequirements] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openAwaitingApproval, setOpenAwaitingApproval] = useState(false);
  const [openActivityModal, setOpenActivityModal] = useState(false);
  const [openEndorsementModal, setOpenEndorsementModal] = useState(false);
  const [currentId, setCurrentId] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const PAGE_SIZE = 10;
  const { notification } = App.useApp();
  const memberTypeId = user?.MembershipTypeId as number;
  const isAssociateOrFull =
    memberTypeId === MemberType.Associate || memberTypeId === MemberType.Full;
  useRequery({
    modalContent: content,
    onOpen: async () => {
      await checkStatus.refetch();
    },
  });

  const columns: ColumnsType<HRWorkActivity> = [
    {
      title: "Date Added",
      dataIndex: "CreatedDate",
      key: "Id",
      render: (_, { CreatedDate }) => formatDate(CreatedDate),
    },
    {
      title: "Activity Name",
      dataIndex: "NameOfTraining",
      key: "Id",
    },
    {
      title: "Score",
      key: "Id",
      dataIndex: "Score",
    },
    {
      title: "Action",
      key: "Action",
      render: (_, { Id, HasApproved }) => {
        return (
          <Button
            className={
              HasApproved === null ? styles.deleteBtn : styles.deleteDisabled
            }
            onClick={() => {
              setCurrentId(Id);
              deleteActivityHandler(Id);
            }}
            text={
              Id === currentId && deleteActivityMutation.isPending
                ? "Deleting..."
                : "Delete"
            }
            disabled={HasApproved !== null || deleteActivityMutation.isPending}
          />
        );
      },
    },
  ];
  const deleteActivityMutation = useMutation({
    mutationFn: deleteWorkActivity,
  });

  const requestUpgradeMutation = useMutation({
    mutationKey: ["request-upgrade"],
    mutationFn: requestMembershipUpgrade,
  });

  const checkStatus = useQuery({
    queryKey: ["check-status"],
    queryFn: () => getMembershipUpgradeStatus(profile?.MembershipNo),
  });

  const activities = useQuery({
    queryKey: ["get-hr-work-activity"],
    queryFn: () => getHRWorkActivity(user?.MemberId!),
  });

  const cutOffScores = useQuery({
    queryKey: ["get-cut-off-scores"],
    queryFn: getCutOffScores,
  });

  const upgradeCutOffScore = cutOffScores.data?.Data
    ?.UpgradeScore as UpgradeScore[];
  const activityData = activities?.data?.Data as HRWorkActivity[];
  const filteredActivityData = activityData?.filter((x) => x.CategoryId === 1);
  const status = checkStatus?.data?.Data as MembershipUpgradeStatus;
  const isAwaitingApproval =
    !status?.HasInvited &&
    !status?.HasPaid &&
    status?.UpgradeHistories?.some((x) => x.IsApproved);
  const isDeclined = status?.UpgradeHistories?.some(
    (x) => x.IsApproved === false
  );
  const timeForInterview = status?.HasInvited && !status?.RequiredPayment;
  const timeForPayment =
    status?.HasInvited && status?.RequiredPayment && !status?.HasPaid;
  const waitingForCertificate = status?.HasPaid && !status?.IsReady;
  const isCertificateReady = status?.IsReady;

  const getScoreByMembershipType = (memberTypeId: number) => {
    const memberTypeKey = MemberType[memberTypeId];
    if (!memberTypeKey) return 0;

    const upgrade = upgradeCutOffScore?.find((x) =>
      x.Name.includes(memberTypeKey)
    );
    return upgrade ? upgrade.Score : 0;
  };

  const notifyMessage = () => {
    if (isAwaitingApproval)
      return <label className={styles.waiting}>Awaiting Approval</label>;
    else if (isDeclined)
      return <label className={styles.declined}>Request Declined</label>;
    else if (timeForInterview)
      return (
        <div className={styles.interview}>
          <label>Time for Interview</label>
          <div className={styles.textWithIcon}>
            <Calendar />
            {status?.InterviewDetail?.InterviewDate
              ? getDateWithoutTime(status?.InterviewDetail?.InterviewDate, true)
              : "N/A"}
          </div>
          <div className={styles.textWithIcon}>
            <Clock />
            {status?.InterviewDetail?.Time}
          </div>
          <div className={styles.textWithIcon}>
            <Marker />
            {status?.InterviewDetail?.InterviewVenue}
          </div>
        </div>
      );
    else if (timeForPayment)
      return <label className={styles.payment}>Make Payment</label>;
    else if (waitingForCertificate) {
      return <label className={styles.payment}>Certificate Processing</label>;
    } else if (isCertificateReady)
      return <label className={styles.payment}>Certificate Ready</label>;
    return <label className={styles.waiting}>Request Initiated</label>;
  };

  const requestUpgrade = async () => {
    try {
      await requestUpgradeMutation.mutateAsync(profile?.MembershipNo, {
        onSuccess: () => {
          setOpenAwaitingApproval(true);
          checkStatus.refetch();
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: errorMessage(error),
      });
    }
  };

  const deleteActivityHandler = async (id: number) => {
    try {
      await deleteActivityMutation.mutateAsync(id, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.Message,
          });
          activities.refetch();
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: errorMessage(error),
      });
    }
  };

  if (checkStatus.isLoading)
    return <Spin style={{ justifySelf: "center", alignSelf: "center" }} />;
  if (
    checkStatus.isError &&
    (checkStatus.error as AxiosError as any)?.response?.data?.Message !==
      "There is no ongoing request"
  )
    return <div>{(checkStatus.error as AxiosError)?.message}</div>;

  return (
    <div>
      <div className={styles.heading}>
        <h3>Profile Details</h3>
        <div className={styles.buttonGroup}>
          <Button
            className={styles.lightBtn}
            onClick={() => setOpenEndorsementModal(true)}
            text="Request Endorsement"
          />
          <Button
            className={styles.lightBtn}
            onClick={() => setOpenActivityModal(true)}
            text="Add Activity"
          />
          <Button
            onClick={() => setOpenRequirements(true)}
            text="Request for Upgrade"
            className={styles.btn}
            isLoading={requestUpgradeMutation.isPending}
            disabled={requestUpgradeMutation.isPending}
          />
        </div>
      </div>
      {checkStatus.isSuccess && (
        <div className={styles.status}>
          <div className={styles.stat}>
            <img src={laptop} alt="laptop" />
            <div>
              <h2>{profile?.Name}</h2>
              {notifyMessage()}
            </div>
          </div>
          {status?.UpgradeHistories?.length > 0 && (
            <Button
              className={classNames("outlineBtn")}
              text="View Details"
              onClick={() => {
                queryClient.refetchQueries({ queryKey: ["get-profile"] });
                navigate("/membership-upgrade/details");
              }}
            />
          )}
        </div>
      )}
      <section className={styles.profileContainer}>
        <ProfileCard showUpgradeScore />
        <br />
        {isAssociateOrFull ? (
          <div className={styles.tableHeading}>
            <img src={points} alt="points" />
            <div>
              <div className={styles.amount}>
                {getScoreByMembershipType(memberTypeId)}
              </div>
              <label>POINTS NEEDED</label>
            </div>
          </div>
        ) : null}
        <Table
          columns={columns}
          dataSource={filteredActivityData}
          pagination={{
            current: pageNumber,
            pageSize: PAGE_SIZE,
            total: filteredActivityData?.length,
            position: ["bottomRight"],
            onChange: (page) => setPageNumber(page),
          }}
          scroll={{ x: true }}
          className={classNames("table")}
          locale={{ emptyText: "No work activities yet" }}
        />
      </section>
      <EndorsementModal
        open={openEndorsementModal}
        closeModal={() => setOpenEndorsementModal(false)}
      />
      <AddActivity
        open={openActivityModal}
        closeModal={() => setOpenActivityModal(false)}
      />
      <Modal
        centered
        open={openAwaitingApproval}
        onCancel={() => setOpenAwaitingApproval(false)}
        footer={null}
      >
        <div className={styles.modalContent}>
          <img src={warning} alt="warning" />
          <h2>Congratulations!!!</h2>
          <p>
            Your request for membership upgrade is sent successfully. Kindly
            await admin approval.
          </p>
          <div className={styles.buttonGroup}>
            <Button
              onClick={() => setOpenAwaitingApproval(false)}
              text="Cancel"
              className={classNames("outlineBtn")}
            />
            <Button onClick={() => setOpenAwaitingApproval(false)} text="Ok" />
          </div>
        </div>
      </Modal>
      <Modal
        centered
        open={openRequirements}
        onCancel={() => setOpenRequirements(false)}
        footer={null}
      >
        <div className={styles.modalContent}>
          <img src={warning} alt="warning" />
          <h2>Upgrade Requirements</h2>
          <p>
            To apply for membership upgrade, you need to meet the following
            requirements:
          </p>
          <ul className={styles.list}>
            <li>
              Eligibility once every{" "}
              {user?.MembershipTypeId === MemberType.Full ? "7" : "6"} years
            </li>
            <li>An active license</li>
            <li>No outstanding subscription dues</li>
            <li>At least {getScoreByMembershipType(memberTypeId)} points</li>
          </ul>
          <div className={styles.buttonGroup}>
            <Button
              onClick={() => setOpenRequirements(false)}
              text="Cancel"
              className={classNames("outlineBtn")}
            />
            <Button
              onClick={() => {
                setOpenRequirements(false);
                setOpenConfirm(true);
              }}
              text="Proceed"
            />
          </div>
        </div>
      </Modal>
      <Modal
        centered
        open={openConfirm}
        onCancel={() => setOpenConfirm(false)}
        footer={null}
      >
        <div className={styles.modalContent}>
          <img src={warning} alt="warning" />
          <h2>Confirm Personal Details</h2>
          <p>
            Please confirm if your personal details are correct before applying
            for upgrade:
          </p>
          <ul className={styles.list}>
            <li>Name</li>
            <li>Email address</li>
            <li>Designation</li>
            <li>Phone number</li>
            <li>Profile picture</li>
            <li>Organization name</li>
            <li>Sector</li>
          </ul>
          <div className={styles.buttonGroup}>
            <Button
              onClick={() => {
                navigate("/profile");
              }}
              text="Go to Profile"
              className={classNames("outlineBtn")}
            />
            <Button
              onClick={() => {
                setOpenConfirm(false);
                requestUpgrade();
              }}
              text="Proceed to Apply"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Main;
